import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  useConfig,
  useTriggerHandshake,
  useUpdateConfig,
} from '@/hooks/use-api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import LoadingSpinner from '@/components/LoadingSpinner';
import {
  ChevronLeft,
  Copy,
  Check,
  Settings,
  Key,
  Activity,
  CheckCircle,
  XCircle,
  RefreshCw,
  Edit2,
  Save,
  X,
} from 'lucide-react';

const ConfigDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: configResponse, isLoading, error } = useConfig(id || '');
  const triggerHandshakeMutation = useTriggerHandshake();

  // Extract config from response structure
  const config = configResponse?.data?.config;

  console.log('ConfigDetailPage - id:', id);
  console.log('ConfigDetailPage - config:', config);
  console.log('ConfigDetailPage - isLoading:', isLoading);
  console.log('ConfigDetailPage - error:', error);

  const [copiedStates, setCopiedStates] = React.useState<
    Record<string, boolean>
  >({});
  const [isEditingProvider, setIsEditingProvider] = React.useState(false);
  const [isEditingSenderId, setIsEditingSenderId] = React.useState(false);
  const [providerFormData, setProviderFormData] = React.useState({
    providerApiKey: '',
  });
  const [senderIdFormData, setSenderIdFormData] = React.useState({
    senderId: undefined as string | undefined,
  });
  const updateConfigMutation = useUpdateConfig();

  // Check if any changes were made
  const hasChanges = React.useMemo(() => {
    return (
      providerFormData.providerApiKey.length > 0 ||
      Boolean(senderIdFormData.senderId && senderIdFormData.senderId.length > 0)
    );
  }, [providerFormData, senderIdFormData]);

  // Initialize form data when config loads
  React.useEffect(() => {
    if (config) {
      setProviderFormData({
        providerApiKey: '',
      });
      setSenderIdFormData({
        senderId: undefined,
      });
    }
  }, [config]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !config) {
    console.log(
      'ConfigDetailPage - showing error state, config:',
      config,
      'error:',
      error
    );
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-destructive mb-4">
            Configuration Not Found
          </h2>
          <p className="text-muted-foreground mb-4">
            {(error as any)?.message ||
              'The requested configuration could not be found.'}
          </p>
          <Link to="/dashboard">
            <Button className="btn-primary">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates((prev) => ({ ...prev, [key]: true }));
      toast.success('Copied to clipboard!');

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedStates((prev) => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleSaveProviderConfig = async () => {
    if (!id) return;

    try {
      const updateData: any = {};

      // Add provider API key if provided
      if (providerFormData.providerApiKey) {
        updateData.providerApiKey = providerFormData.providerApiKey;
      }

      // Add sender ID if provided (this will reset handshake status)
      if (
        senderIdFormData.senderId !== undefined &&
        senderIdFormData.senderId !== config.senderId
      ) {
        // Check if sender ID already exists in other configs
        const { configAPI } = await import('@/lib/api');
        const allConfigs = await configAPI.getAll();
        const duplicateConfig = allConfigs.find(
          (c: any) => c.senderId === senderIdFormData.senderId && c._id !== id
        );

        if (duplicateConfig) {
          toast.error(
            `Sender ID "${senderIdFormData.senderId}" is already used by another configuration. Please use a different sender ID.`
          );
          return;
        }

        updateData.senderId = senderIdFormData.senderId;
        // Reset handshake status when changing sender ID
        updateData.handshakeCompleted = false; // Reset handshake status
        updateData.handshakeCompletedAt = null; // Clear handshake timestamp
      }

      await updateConfigMutation.mutateAsync({
        id,
        data: updateData,
      });

      setIsEditingProvider(false);
      setIsEditingSenderId(false);

      // Reset form fields after save
      setProviderFormData((prev) => ({ ...prev, providerApiKey: '' }));
      setSenderIdFormData((prev) => ({ ...prev, senderId: undefined }));

      // Show appropriate success message
      if (senderIdFormData.senderId) {
        toast.success(
          'Sender ID updated! Configuration requires re-handshake.'
        );
      } else if (providerFormData.providerApiKey) {
        toast.success('API Key updated successfully!');
      }
    } catch (error: any) {
      console.error('Configuration update error:', error);
      console.log('Error response:', error.response);
      console.log('Error response data:', error.response?.data);

      // Handle validation errors from server
      if (error.response?.data?.errors) {
        // Clear any existing toasts first
        toast.dismiss();

        console.log('Handling validation errors:', error.response.data.errors);

        error.response.data.errors.forEach((err: any) => {
          console.log('Processing error:', err);
          console.log('Error path:', err.path);
          console.log('Error field:', err.field);
          console.log('Error code:', err.code);

          // Check both path and field for providerApiKey
          const isProviderApiKeyError =
            (err.path &&
              (err.path.includes('providerApiKey') ||
                err.path === 'providerApiKey')) ||
            (err.field &&
              (err.field.includes('providerApiKey') ||
                err.field === 'providerApiKey'));

          if (isProviderApiKeyError) {
            toast.error(
              err.message?.toLowerCase().includes('too small')
                ? 'API Key must be at least 10 characters long'
                : `API Key: ${err.message}`
            );
          } else {
            const fieldName = err.field || err.path || 'unknown';
            console.log('Showing field error:', fieldName, err.message);
            toast.error(`${fieldName}: ${err.message}`);
          }
        });
        return; // Stop here to prevent further error handling
      }

      // Handle generic validation error message
      if (error.response?.data?.message === 'Validation error') {
        toast.dismiss(); // Clear any existing toasts
        console.log('Generic validation error, not showing toast');
        return; // Don't show generic message when we have field errors
      }

      // Handle other errors
      console.log('Handling other error:', error.response?.data?.message);
      toast.error(
        error.response?.data?.message || 'Failed to update configuration'
      );
    }
  };

  const handleCancelEdit = () => {
    setIsEditingProvider(false);
    setIsEditingSenderId(false);
    // Reset form to original values
    setProviderFormData({
      providerApiKey: '',
    });
    setSenderIdFormData({
      senderId: undefined,
    });
  };

  const handleTriggerHandshake = async () => {
    try {
      await triggerHandshakeMutation.mutateAsync(config._id);
      // Toast messages are handled by the mutation hook
    } catch (error: any) {
      // Error toast is handled by the mutation hook
      console.error('Handshake error:', error);
    }
  };

  const getStatusIcon = (completed: boolean) => {
    return completed ? (
      <CheckCircle className="w-5 h-5 text-green-500" />
    ) : (
      <XCircle className="w-5 h-5 text-red-500" />
    );
  };

  const getStatusText = (completed: boolean) => {
    return completed ? 'Connected' : 'Not Connected';
  };

  const getStatusColor = (completed: boolean) => {
    return completed
      ? 'text-green-700 bg-green-50 border-green-200'
      : 'text-red-700 bg-red-50 border-red-200';
  };

  // Generate webhook URLs based on configuration
  const baseUrl = window.location.origin;
  const webhookUrls = {
    deliveryReceipt: `${baseUrl}/api/webhook/provider/dlr/${config._id}`,
    inboundSms: `${baseUrl}/api/webhook/provider/inbound/${config._id}`,
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white shadow-sm border-b header-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <img
                  src="/onextel-logo-white.png"
                  alt="Onextel Logo"
                  className="h-12 w-auto object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-primary">
                  Optimove - Onextel Setup
                </h1>
                <p className="text-xs text-muted">SMS Integration Dashboard</p>
              </div>
            </div>
            <Link
              to="/dashboard"
              className="flex items-center text-primary hover:text-accent transition-colors duration-300 font-semibold text-sm"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {config ? (
          <>
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-3xl font-bold text-primary mb-2 animate-fade-in break-all">
                    {config.senderId || 'Unknown Sender'}
                  </h1>
                  <p
                    className="text-lg text-muted animate-slide-up"
                    style={{ animationDelay: '0.2s' }}
                  >
                    Configuration Details
                  </p>
                </div>

                <div className="flex items-center space-x-3">
                  <div
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full border text-sm font-bold transition-all duration-300 hover:scale-105 ${getStatusColor(config.handshakeCompleted)}`}
                  >
                    {getStatusIcon(config.handshakeCompleted)}
                    <span>{getStatusText(config.handshakeCompleted)}</span>
                  </div>

                  {!config.handshakeCompleted && (
                    <Button
                      onClick={handleTriggerHandshake}
                      disabled={triggerHandshakeMutation.isPending}
                      className="btn-primary transition-all duration-300 hover:scale-105"
                    >
                      <RefreshCw
                        className={`w-4 h-4 mr-2 ${triggerHandshakeMutation.isPending ? 'animate-spin' : ''}`}
                      />
                      {triggerHandshakeMutation.isPending
                        ? 'Connecting...'
                        : 'Connect'}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Configuration Details */}
            <div className="grid gap-8 lg:grid-cols-2">
              {/* Optimove Configuration */}
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="border rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center animate-fade-in">
                      <Key className="w-5 h-5 mr-2 text-accent" />
                      Optimove Configuration
                    </h2>
                  </div>

                  <div className="space-y-4">
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <label className="text-sm font-medium text-muted-foreground">
                        Base URL
                      </label>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-sm text-foreground break-all font-medium">
                          {config.optimoveBaseUrl}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              config.optimoveBaseUrl,
                              'optimoveBaseUrl'
                            )
                          }
                        >
                          {copiedStates['optimoveBaseUrl'] ? (
                            <Check className="w-4 h-4 text-accent" />
                          ) : (
                            <Copy className="w-4 h-4 text-muted" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <label className="text-sm font-medium text-muted-foreground">
                        API Token
                      </label>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-sm text-foreground font-mono font-medium">
                          {'*'.repeat(config.optimoveApiToken.length)}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              config.optimoveApiToken,
                              'optimoveApiToken'
                            )
                          }
                        >
                          {copiedStates['optimoveApiToken'] ? (
                            <Check className="w-4 h-4 text-accent" />
                          ) : (
                            <Copy className="w-4 h-4 text-muted" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <label className="text-sm font-medium text-muted-foreground">
                        Secret Key
                      </label>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-sm text-foreground font-mono font-medium">
                          {'*'.repeat(config.optimoveSecretKey?.length || 20)}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              config.optimoveSecretKey || '[Hidden]',
                              'optimoveSecretKey'
                            )
                          }
                        >
                          {copiedStates['optimoveSecretKey'] ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center animate-fade-in">
                      <Settings className="w-5 h-5 mr-2 text-accent" />
                      Basic Information
                    </h2>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <label className="text-sm font-medium text-muted-foreground">
                        Configuration ID
                      </label>
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-mono text-foreground font-medium">
                          {config._id}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            copyToClipboard(config._id, 'configId')
                          }
                        >
                          {copiedStates['configId'] ? (
                            <Check className="w-4 h-4 text-accent" />
                          ) : (
                            <Copy className="w-4 h-4 text-muted" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <label className="text-sm font-medium text-muted-foreground">
                        Created
                      </label>
                      <p className="text-sm text-foreground mt-1 font-medium">
                        {new Date(config.createdAt).toLocaleString()}
                      </p>
                    </div>

                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <label className="text-sm font-medium text-muted-foreground">
                        Last Updated
                      </label>
                      <p className="text-sm text-foreground mt-1 font-medium">
                        {new Date(config.updatedAt).toLocaleString()}
                      </p>
                    </div>

                    {config.handshakeCompletedAt && (
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <label className="text-sm font-medium text-muted-foreground">
                          Connected Since
                        </label>
                        <p className="text-sm text-foreground mt-1 font-medium">
                          {new Date(
                            config.handshakeCompletedAt
                          ).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Provider Configuration */}
              <div className="space-y-6">
                <div className="border rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center animate-fade-in">
                      <Key className="w-5 h-5 mr-2 text-accent" />
                      Onextel Configuration
                    </h2>
                  </div>

                  {isEditingProvider && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> Updating the provider API key
                        will not disconnect your configuration. You can continue
                        using the service without reconnection.
                      </p>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Sender ID - Editable */}
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Sender ID
                      </label>
                      {isEditingSenderId ? (
                        <div className="space-y-2">
                          <Input
                            type="text"
                            value={
                              senderIdFormData.senderId !== undefined
                                ? senderIdFormData.senderId
                                : config.senderId
                            }
                            onChange={(e) =>
                              setSenderIdFormData((prev) => ({
                                ...prev,
                                senderId: e.target.value,
                              }))
                            }
                            className="mt-1"
                            placeholder="Enter new Sender ID"
                          />
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleCancelEdit}
                              disabled={updateConfigMutation.isPending}
                            >
                              <X className="w-4 h-4 mr-2" />
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveProviderConfig}
                              disabled={
                                updateConfigMutation.isPending ||
                                !senderIdFormData.senderId?.trim()
                              }
                            >
                              <Check className="w-4 h-4 mr-2" />
                              Save
                            </Button>
                          </div>
                          <p className="text-xs text-amber-600">
                            <strong>Warning:</strong> Changing Sender ID will
                            disconnect the configuration and require
                            re-handshake.
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-sm text-foreground">
                            {config.senderId}
                          </p>
                          <div className="flex space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setIsEditingSenderId(true)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                copyToClipboard(config.senderId, 'senderId')
                              }
                            >
                              {copiedStates['senderId'] ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* API Key */}
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        API Key
                      </label>
                      {isEditingProvider ? (
                        <div className="space-y-2">
                          <Input
                            type="password"
                            value={providerFormData.providerApiKey}
                            onChange={(e) =>
                              setProviderFormData((prev) => ({
                                ...prev,
                                providerApiKey: e.target.value,
                              }))
                            }
                            className="mt-1"
                            placeholder="Enter new API Key (leave empty to keep current)"
                          />
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleCancelEdit}
                              disabled={updateConfigMutation.isPending}
                            >
                              <X className="w-4 h-4 mr-2" />
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveProviderConfig}
                              disabled={
                                updateConfigMutation.isPending || !hasChanges
                              }
                            >
                              <Save className="w-4 h-4 mr-2" />
                              {updateConfigMutation.isPending
                                ? 'Saving...'
                                : 'Save'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-sm text-foreground font-mono">
                            {'*'.repeat(config.providerApiKey?.length || 20)}
                          </p>
                          <div className="flex items-center space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                copyToClipboard(
                                  config.providerApiKey || '[Hidden]',
                                  'providerApiKey'
                                )
                              }
                            >
                              {copiedStates['providerApiKey'] ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setIsEditingProvider(true)}
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Webhook URLs */}
                <div className="border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                    <Activity className="w-5 h-5 mr-2" />
                    Webhook URLs
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Delivery Receipt
                      </label>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-sm text-foreground font-mono break-all">
                          {webhookUrls.deliveryReceipt}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              webhookUrls.deliveryReceipt,
                              'deliveryReceipt'
                            )
                          }
                        >
                          {copiedStates['deliveryReceipt'] ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Inbound SMS
                      </label>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-sm text-foreground font-mono break-all">
                          {webhookUrls.inboundSms}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              webhookUrls.inboundSms,
                              'inboundSms'
                            )
                          }
                        >
                          {copiedStates['inboundSms'] ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              No configuration data available
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ConfigDetailPage;
